import { useDispatch } from "react-redux";
import { AppDispatch, useSelector } from "@/core/redux/store";
import { useEffect } from "react";
import CardTabs from "./card-tabs/card-tabs";
import BarChart from "./bar-chart/bar-chart";
import { getProcessedResources } from "@/core/redux/thunks/dashboardThunk";
import { Skeleton } from "@/shared/components/ui/skeleton";

type ChartDataItem = {
  month: string;
  background: number;
  red: number;
  orange: number;
  yellow: number;
};

export const formatNumber = (number: number) => {
  return isNaN(number) ? 0 : number.toLocaleString();
};

// Tab bazlı data - her tab için farklı veri
const getChartDataForTab = (tabIndex: number): ChartDataItem[] => {
  const baseData: ChartDataItem[] = [
    { month: "J", background: 0, red: 14, orange: 6, yellow: 10 },
    { month: "F", background: 0, red: 2, orange: 16, yellow: 8 },
    { month: "M", background: 0, red: 14, orange: 8, yellow: 12 },
    { month: "A", background: 0, red: 6, orange: 14, yellow: 6 },
    { month: "M", background: 0, red: 15, orange: 10, yellow: 4 },
    { month: "J", background: 0, red: 15, orange: 9, yellow: 6 },
    { month: "J", background: 0, red: 16, orange: 8, yellow: 8 },
    { month: "A", background: 0, red: 9, orange: 10, yellow: 10 },
    { month: "S", background: 0, red: 8, orange: 12, yellow: 8 },
    { month: "O", background: 0, red: 8, orange: 10, yellow: 10 },
    { month: "N", background: 0, red: 14, orange: 7, yellow: 8 },
    { month: "D", background: 0, red: 8, orange: 12, yellow: 14 },
  ];

  // Tab 0: PDF & Döküman - yellow odaklı
  if (tabIndex === 0) {
    return baseData.map((item) => ({
      ...item,
      yellow: item.yellow + 8, // Sarı artır
      red: Math.max(0, item.red - 2),
      orange: Math.max(0, item.orange - 3),
    }));
  }

  // Tab 1: Web Kaynakları - orange odaklı
  if (tabIndex === 1) {
    return baseData.map((item) => ({
      ...item,
      orange: item.orange + 5, // Turuncu artır
      red: Math.max(0, item.red - 2),
      yellow: Math.max(0, item.yellow - 3),
    }));
  }

  // Tab 2: Multimedia - red odaklı
  if (tabIndex === 2) {
    return baseData.map((item) => ({
      ...item,
      red: item.red + 12, // Kırmızı artır
      orange: Math.max(0, item.orange - 2),
      yellow: Math.max(0, item.yellow - 1),
    }));
  }

  return baseData;
};

export default function ProcessedSources() {
  const processedResources = useSelector((state) => state.dashboard.processedResources);
  const isLoading = useSelector((state) => state.dashboard.loadingProcessedResources);
  const dispatch = useDispatch<AppDispatch>();
  const userDetail = useSelector((state) => state.user.me);
  const company = userDetail?.company;
  const filterParams = useSelector((state) => state.dashboard.filterParams);
  useEffect(() => {
    if (!company?.id) return;
    dispatch(getProcessedResources({ companyId: company.id, ...filterParams }));
  }, [company?.id, filterParams]);
  const {
    totalAnalyzedProduct = 0,
    totalItem = 0,
    pdfDocumentCount,
    pageReadCount = 0,
    webSourcesCount = 0,
    multimediaVideosCount = 0,
    hoursOfWatch = 0,
  } = processedResources ?? {};

  return (
    <div className="w-full h-full rounded-[16px] border border-[#e5e5e5] bg-white">
      <div className="border-b border-[#e4e4e7] h-[50px] flex items-center px-[20px]">
        <p className="text-[16px] font-[700] text-[#000000] leading-[24px]">
          İşlenen Kaynaklar
        </p>
      </div>
      <div className="flex flex-shrink-0 h-[96px]">
        <div className="flex-1 px-[20px] pt-[20px] ">
          <div className="flex flex-col border-r border-[#e4e4e7]">
            <div className="text-[12px] text-muted-foreground font-600">
              Analiz Edilen Ürün
            </div>
            <div className="font-[Inter] font-[700] text-[24px] text-black ">
              {isLoading ? <Skeleton className="w-[80px] h-[24px] mt-[10px]" /> : formatNumber(totalAnalyzedProduct)}
            </div>
          </div>
        </div>
        <div className="flex-1 px-[20px] pt-[20px] ">
          <div className="flex flex-col ">
            <div className="text-[12px] text-muted-foreground font-600">
              Toplam Madde
            </div>
            <div className="font-[Inter] font-[700] text-[24px] text-black ">
              {isLoading ? <Skeleton className="w-[80px] h-[24px] mt-[10px]" /> : formatNumber(totalItem)}
            </div>
          </div>
        </div>
      </div>

      <CardTabs
        isLoading={isLoading}
        pdfDocumentCount={pdfDocumentCount ?? 0}
        webSourcesCount={webSourcesCount ?? 0}
        hoursOfWatch={hoursOfWatch ?? 0}
        pageReadCount={pageReadCount ?? 0}
        multimediaVideosCount={multimediaVideosCount ?? 0}
      />

      <BarChart data={getChartDataForTab(0)} />
    </div>
  );
}
